import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import EditIcon from "@mui/icons-material/Edit"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import PlaceIcon from "@mui/icons-material/Place"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material"
import type {
  BiomeshContactRef,
  BiomeshContactResponse,
  BiomeshOperationResponse,
  BiomeshPlatformResponse,
  BiomeshSensorResponse,
  BiomeshUnitRef,
  BiomeshUnitResponse,
} from "@phc/common/gen/threatAnalyticsGen"
import {
  getListOperationsBiomeshOperationListGetQueryKey,
  getListPlatformsBiomeshPlatformListGetQueryKey,
  useUpdateOperationBiomeshOperationOperationIdPut,
  useUpdatePlatformBiomeshPlatformPlatformIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { useCallback, useMemo, useState } from "react"
import { extraColors } from "../../utils/theme"
import { ContactFormDialog } from "../Cop/ContactFormDialog"
import { parseCentroid, toCentroid } from "../Cop/copUtils"
import { DeleteContactDialog } from "../Cop/DeleteContactDialog"
import { DeleteNodeDialog } from "../Cop/DeleteNodeDialog"
import { DeleteOperationDialog } from "../Cop/DeleteOperationDialog"
import { DeleteSensorDialog } from "../Cop/DeleteSensorDialog"
import { DeleteUnitDialog } from "../Cop/DeleteUnitDialog"
import { NodeFormDialog } from "../Cop/NodeFormDialog"
import { OperationFormDialog } from "../Cop/OperationFormDialog"
import { SensorFormDialog } from "../Cop/SensorFormDialog"
import { UnitFormDialog } from "../Cop/UnitFormDialog"
import { ContactCard } from "./cards/ContactCard"
import { OperationCard } from "./cards/OperationCard"
import { SensorCard } from "./cards/SensorCard"
import { UnitCard } from "./cards/UnitCard"

/* ── styled primitives ── */

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: extraColors.dark5,
  "&:before": { display: "none" },
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  overflow: "hidden",
}))

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: 1,
  marginRight: theme.spacing(1),
}))

const SensorCountChip = styled(Chip)({
  backgroundColor: extraColors.dark10,
  color: extraColors.medium,
  fontSize: 11,
  height: 20,
})

const HeaderActions = styled(Box)(({ theme }) => ({
  marginLeft: "auto",
  display: "flex",
  gap: theme.spacing(0.5),
}))

const ActionButton = styled(IconButton)({
  color: extraColors.hint,
}) as typeof IconButton

const CompactTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minHeight: 32,
}))

const CompactTab = styled(Tab)({
  minHeight: 32,
  paddingTop: 4,
  paddingBottom: 4,
})

const AddButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}))

const EmptyState = styled(Typography)(({ theme }) => ({
  color: extraColors.hint,
  textAlign: "center",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}))

/* ── types ── */

interface NodeAccordionProps {
  node: BiomeshPlatformResponse
  sensors: BiomeshSensorResponse[]
  operationMap: Map<string, BiomeshOperationResponse>
  unitMap: Map<string, BiomeshUnitResponse>
  contactMap: Map<string, BiomeshContactResponse>
  unmapped?: boolean
  onPlaceOnMap?: () => void
}

export function NodeAccordion({
  node,
  sensors,
  operationMap,
  unitMap,
  contactMap,
  unmapped,
  onPlaceOnMap,
}: NodeAccordionProps) {
  const [tab, setTab] = useState(0)
  const [editNodeOpen, setEditNodeOpen] = useState(false)
  const [deleteNodeOpen, setDeleteNodeOpen] = useState(false)
  const [addSensorOpen, setAddSensorOpen] = useState(false)
  const [editSensor, setEditSensor] = useState<BiomeshSensorResponse | null>(
    null
  )
  const [deleteSensor, setDeleteSensor] =
    useState<BiomeshSensorResponse | null>(null)
  const [editContact, setEditContact] = useState<BiomeshContactResponse | null>(
    null
  )
  const [deleteContact, setDeleteContact] =
    useState<BiomeshContactResponse | null>(null)
  const [addUnitOpen, setAddUnitOpen] = useState(false)
  const [editUnit, setEditUnit] = useState<BiomeshUnitResponse | null>(null)
  const [deleteUnit, setDeleteUnit] = useState<BiomeshUnitResponse | null>(null)
  const [addOperationOpen, setAddOperationOpen] = useState(false)
  const [editOperation, setEditOperation] =
    useState<BiomeshOperationResponse | null>(null)
  const [deleteOperation, setDeleteOperation] =
    useState<BiomeshOperationResponse | null>(null)

  const queryClient = useQueryClient()
  const { mutateAsync: updatePlatform } =
    useUpdatePlatformBiomeshPlatformPlatformIdPut()
  const { mutateAsync: updateOperation } =
    useUpdateOperationBiomeshOperationOperationIdPut()

  const handleUnlinkOperation = useCallback(
    async (operationId: string) => {
      const currentOpIds = (node.operations ?? [])
        .map(o => o.operation_id)
        .filter((id): id is string => !!id)
      const coords = parseCentroid(node.centroid)
      try {
        await updatePlatform({
          platformId: node.platform_id,
          data: {
            name: node.name,
            platform_type: node.platform_type,
            centroid: coords ? toCentroid(coords.lat, coords.lng) : undefined,
            operation_ids: currentOpIds.filter(id => id !== operationId),
          },
        })
        await queryClient.invalidateQueries({
          queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
        })
        enqueueSnackbar("Operation removed from node", { variant: "success" })
      } catch {
        enqueueSnackbar("Failed to remove operation", { variant: "error" })
      }
    },
    [node, queryClient, updatePlatform]
  )

  const nodeOperations = useMemo(
    () =>
      (node.operations ?? []).flatMap(ref => {
        const id = ref.operation_id
        if (!id) return []
        const op = operationMap.get(id)
        return op ? [op] : []
      }),
    [node.operations, operationMap]
  )

  const { contacts, contactViaMap } = useMemo(() => {
    const viaMap = new Map<string, string[]>()
    const refMap = new Map<string, BiomeshContactRef>()

    for (const op of nodeOperations) {
      for (const c of op.contacts ?? []) {
        const id = c.contact_id ?? ""
        if (!id) continue
        if (!refMap.has(id)) refMap.set(id, c)
        const labels = viaMap.get(id) ?? []
        labels.push(`Op: ${op.name}`)
        viaMap.set(id, labels)
      }
    }

    for (const s of sensors) {
      for (const c of s.contacts ?? []) {
        const id = c.contact_id ?? ""
        if (!id) continue
        if (!refMap.has(id)) refMap.set(id, c)
        const labels = viaMap.get(id) ?? []
        labels.push(`Sensor: ${s.sensor_name}`)
        viaMap.set(id, labels)
      }
    }

    return {
      contacts: [...refMap.values()],
      contactViaMap: viaMap,
    }
  }, [nodeOperations, sensors])

  const units = useMemo(() => {
    const seen = new Set<string>()
    const result: BiomeshUnitRef[] = []
    for (const op of nodeOperations) {
      for (const u of op.units ?? []) {
        const id = u.unit_id ?? ""
        if (id && !seen.has(id)) {
          seen.add(id)
          result.push(u)
        }
      }
    }
    return result
  }, [nodeOperations])

  const handleOperationSaved = useCallback(
    async (operationId: string) => {
      const currentOpIds = (node.operations ?? [])
        .map(o => o.operation_id)
        .filter((id): id is string => !!id)
      const coords = parseCentroid(node.centroid)
      try {
        await updatePlatform({
          platformId: node.platform_id,
          data: {
            name: node.name,
            platform_type: node.platform_type,
            centroid: coords ? toCentroid(coords.lat, coords.lng) : undefined,
            operation_ids: [...currentOpIds, operationId],
          },
        })
        await queryClient.invalidateQueries({
          queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
        })
      } catch {
        enqueueSnackbar(
          "Operation created but could not be linked to this node",
          { variant: "warning" }
        )
      }
    },
    [node, queryClient, updatePlatform]
  )

  const handleUnitSaved = useCallback(
    async (unitId: string) => {
      const targetOp = nodeOperations[0]
      if (!targetOp) return
      const currentIds = (targetOp.units ?? [])
        .map(u => u.unit_id)
        .filter((id): id is string => !!id)
      try {
        await updateOperation({
          operationId: targetOp.operation_id,
          data: {
            name: targetOp.name,
            unit_ids: [...currentIds, unitId],
          },
        })
        await queryClient.invalidateQueries({
          queryKey: getListOperationsBiomeshOperationListGetQueryKey(),
        })
        await queryClient.invalidateQueries({
          queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
        })
      } catch {
        enqueueSnackbar("Unit created but could not be linked to operation", {
          variant: "warning",
        })
      }
    },
    [nodeOperations, queryClient, updateOperation]
  )

  return (
    <>
      <StyledAccordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <HeaderRow>
            <Typography variant="body2Bold">{node.name}</Typography>
            <SensorCountChip
              label={`${sensors.length} sensor${sensors.length !== 1 ? "s" : ""}`}
              size="small"
            />
            {unmapped && !node.centroid && (
              <Chip
                label="Unmapped"
                size="small"
                sx={{
                  backgroundColor: "transparent",
                  border: `1px solid ${extraColors.hint}`,
                  color: extraColors.hint,
                  fontSize: 11,
                  height: 20,
                }}
              />
            )}
            <HeaderActions>
              {onPlaceOnMap && (
                <Tooltip title="Place on Map">
                  <ActionButton
                    component="span"
                    size="small"
                    onClick={e => {
                      e.stopPropagation()
                      onPlaceOnMap()
                    }}
                    aria-label="Place on map"
                  >
                    <PlaceIcon fontSize="small" />
                  </ActionButton>
                </Tooltip>
              )}
              <ActionButton
                component="span"
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  setEditNodeOpen(true)
                }}
                aria-label="Edit node"
              >
                <EditIcon fontSize="small" />
              </ActionButton>
              <ActionButton
                component="span"
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  setDeleteNodeOpen(true)
                }}
                aria-label="Delete node"
              >
                <DeleteOutlineIcon fontSize="small" />
              </ActionButton>
            </HeaderActions>
          </HeaderRow>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <CompactTabs
            value={tab}
            onChange={(_, v: number) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            slotProps={{ indicator: { sx: { height: 2 } } }}
          >
            <CompactTab label="Sensors" />
            <CompactTab label="Contacts" />
            <CompactTab label="Units" />
            <CompactTab label="Operations" />
          </CompactTabs>

          {tab === 0 && (
            <>
              <AddButton
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddSensorOpen(true)}
              >
                Add Sensor
              </AddButton>
              {sensors.length === 0 ? (
                <EmptyState variant="body2">No sensors assigned</EmptyState>
              ) : (
                sensors.map(sensor => (
                  <SensorCard
                    key={sensor.sensor_id}
                    sensor={sensor}
                    onEdit={() => setEditSensor(sensor)}
                    onDelete={() => setDeleteSensor(sensor)}
                  />
                ))
              )}
            </>
          )}

          {tab === 1 && (
            <>
              {contacts.length === 0 ? (
                <EmptyState variant="body2">No contacts</EmptyState>
              ) : (
                contacts.map(c => {
                  const fullContact = c.contact_id
                    ? contactMap.get(c.contact_id)
                    : undefined
                  return (
                    <ContactCard
                      key={c.contact_id}
                      contact={c}
                      via={contactViaMap.get(c.contact_id ?? "")}
                      onEdit={
                        fullContact
                          ? () => setEditContact(fullContact)
                          : undefined
                      }
                      onDelete={
                        fullContact
                          ? () => setDeleteContact(fullContact)
                          : undefined
                      }
                    />
                  )
                })
              )}
            </>
          )}

          {tab === 2 && (
            <>
              <Tooltip
                title={
                  nodeOperations.length === 0
                    ? "Add an operation to this node first"
                    : ""
                }
              >
                <span>
                  <AddButton
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAddUnitOpen(true)}
                    disabled={nodeOperations.length === 0}
                  >
                    Add Unit
                  </AddButton>
                </span>
              </Tooltip>
              {units.length === 0 ? (
                <EmptyState variant="body2">No units</EmptyState>
              ) : (
                units.map(u => {
                  const fullUnit = u.unit_id
                    ? unitMap.get(u.unit_id)
                    : undefined
                  return (
                    <UnitCard
                      key={u.unit_id}
                      unit={u}
                      onEdit={
                        fullUnit ? () => setEditUnit(fullUnit) : undefined
                      }
                      onDelete={
                        fullUnit ? () => setDeleteUnit(fullUnit) : undefined
                      }
                    />
                  )
                })
              )}
            </>
          )}

          {tab === 3 && (
            <>
              <AddButton
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddOperationOpen(true)}
              >
                Add Operation
              </AddButton>
              {nodeOperations.length === 0 ? (
                <EmptyState variant="body2">No operations</EmptyState>
              ) : (
                nodeOperations.map(op => (
                  <OperationCard
                    key={op.operation_id}
                    operation={op}
                    onEdit={() => setEditOperation(op)}
                    onUnlink={() => void handleUnlinkOperation(op.operation_id)}
                    onDelete={() => setDeleteOperation(op)}
                  />
                ))
              )}
            </>
          )}
        </AccordionDetails>
      </StyledAccordion>

      <NodeFormDialog
        open={editNodeOpen}
        onClose={() => setEditNodeOpen(false)}
        node={node}
      />
      <DeleteNodeDialog
        open={deleteNodeOpen}
        onClose={() => setDeleteNodeOpen(false)}
        node={node}
      />
      <SensorFormDialog
        open={addSensorOpen}
        onClose={() => setAddSensorOpen(false)}
        initialPlatformId={node.platform_id}
      />
      {editSensor && (
        <SensorFormDialog
          open
          onClose={() => setEditSensor(null)}
          sensor={editSensor}
        />
      )}
      {deleteSensor && (
        <DeleteSensorDialog
          open
          onClose={() => setDeleteSensor(null)}
          sensor={deleteSensor}
        />
      )}
      {editContact && (
        <ContactFormDialog
          open
          onClose={() => setEditContact(null)}
          contact={editContact}
        />
      )}
      {deleteContact && (
        <DeleteContactDialog
          open
          onClose={() => setDeleteContact(null)}
          contact={deleteContact}
        />
      )}
      <UnitFormDialog
        open={addUnitOpen}
        onClose={() => setAddUnitOpen(false)}
        onSaved={id => void handleUnitSaved(id)}
      />
      {editUnit && (
        <UnitFormDialog
          open
          onClose={() => setEditUnit(null)}
          unit={editUnit}
        />
      )}
      {deleteUnit && (
        <DeleteUnitDialog
          open
          onClose={() => setDeleteUnit(null)}
          unit={deleteUnit}
        />
      )}
      <OperationFormDialog
        open={addOperationOpen}
        onClose={() => setAddOperationOpen(false)}
        onSaved={id => void handleOperationSaved(id)}
      />
      {editOperation && (
        <OperationFormDialog
          open
          onClose={() => setEditOperation(null)}
          operation={editOperation}
        />
      )}
      {deleteOperation && (
        <DeleteOperationDialog
          open
          onClose={() => setDeleteOperation(null)}
          operation={deleteOperation}
        />
      )}
    </>
  )
}
